import React, { createContext, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { QueryParameters, Routes } from "@lifeat/shared/system/RouteConstants";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import { hideMenubarWindow, showMainWindow } from "@desktop/ToDesktopService";
import { isWindowMenubar } from "@desktop/ToDesktopUtil";
import { sendClarityEvent } from "@features/clarity/Clarity";
import { ClarityEvent } from "@features/clarity/ClarityEvents";
import { logError } from "@service/ConsoleLoggerService";
import { AdditionalSourceData, DisplayUpgradeCTAEvent, UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { DisplayUserSignUpCtaEvent, SignUpCTASource, SignUpOrUpgradeSource } from "@user/AmplitudeUserEvents";
import { useUserContext } from "./UserContext";

export type UpgradeCtaContextType =
    | "default"
    | "onboarding"
    | "onboardingBlockedFlow"
    | "multipleCalendars"
    | "notes"
    | "pomodoroSounds"
    | "productivityReports"
    | "readOnlyWidget"
    | "unlimitedSounds"
    | "unlimitedVideoChat"
    | "spaces"
    | "tags"
    | "planner"
    | "projects"
    | "taskTimer";

export type CallToActionContextType = {
    showSignInView: boolean;
    hideSignInView: () => void;
    setSignInView: (amplitudeEventSource: SignUpCTASource, additionalSourceData?: AdditionalSourceData) => void;

    showUpgradeCta: boolean;
    hideUpgradeCta: () => void;

    upgradeCtaContextType: UpgradeCtaContextType;
    setUpgradeCtaContext: (
        upgradeContext: UpgradeCtaContextType,
        amplitudeEventSource: UpgradeCTASource,
        additionalSourceData?: AdditionalSourceData,
    ) => void;
};

export const CallToActionContext: React.Context<CallToActionContextType> = createContext<CallToActionContextType>({
    showSignInView: false,
    hideSignInView: () => {},
    setSignInView: () => {},

    showUpgradeCta: false,
    hideUpgradeCta: () => {},

    upgradeCtaContextType: "default",
    setUpgradeCtaContext: () => {},
});

interface CallToActionContextProviderProps {
    children?: React.ReactNode;
}

export function CallToActionContextProvider({ children }: CallToActionContextProviderProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const history = useHistory();
    const { user } = useUserContext();
    const [showSignInView, setShowSignInView] = useState(false);
    const [showUpgradeCta, setShowUpgradeCta] = useState(false);
    const [upgradeCta, setUpgradeCta] = useState<UpgradeCtaContextType>("default");

    const setSignInView = (
        amplitudeEventSource: SignUpOrUpgradeSource,
        additionalSourceData?: AdditionalSourceData,
    ) => {
        setShowSignInView(true);

        GA4Service.logEvent(GA4Event.UPGRADE_BUTTON_CLICK, {
            [GA4EventProperties.upgrade_button_content.key]: GA4EventProperties.upgrade_button_content.authenticate,
        });
        sendClarityEvent(ClarityEvent.UPGRADE_CLICK);
        if (amplitudeEventSource) {
            amplitudeService.logEvent(
                new DisplayUserSignUpCtaEvent({
                    source: amplitudeEventSource,
                    ...(additionalSourceData && { additionalSourceData: additionalSourceData }),
                }),
            );
        }
    };

    const hideSignInView = () => {
        setShowSignInView(false);
    };

    const setUpgradeCtaContext = (
        upgradeContext: UpgradeCtaContextType,
        amplitudeEventSource: UpgradeCTASource,
        additionalSourceData?: AdditionalSourceData,
    ) => {
        if (user == null || user.email == null) {
            logError("User is null.");
            setSignInView(upgradeContext as SignUpOrUpgradeSource);
        } else {
            setUpgradeCta(upgradeContext);

            if (amplitudeEventSource) {
                GA4Service.logEvent(GA4Event.UPGRADE_BUTTON_CLICK, {
                    [GA4EventProperties.upgrade_button_content.key]: GA4EventProperties.upgrade_button_content.upgrade,
                });
                amplitudeService.logEvent(
                    new DisplayUpgradeCTAEvent({
                        accountCreationDate: user.metadata.creationTime,
                        source: amplitudeEventSource,
                        ...(additionalSourceData && { additionalSourceData: additionalSourceData }),
                    }),
                );
                sendClarityEvent(ClarityEvent.UPGRADE_CLICK);
            }

            const targetPath = `/${Routes.PricingTierPage}?${QueryParameters.UpgradeContext}=${upgradeContext || "default"}`;
            if (isWindowMenubar()) {
                // If CTA is triggered from the menubar window,
                // Open the CTA in main window.

                // Because of a glitch in ToDesktop window behavior,
                // UX is more consistent when we explicitly hide the menubar window
                // before opening the upgrade CTA in main window.
                // https://lifeatinc.slack.com/archives/C03GU1HNQ75/p1738675800283729
                hideMenubarWindow().then(() => showMainWindow(new URL(targetPath, window.origin).href));
            } else {
                history.push(targetPath);
            }
        }
    };

    const hideUpgradeCta = () => {
        setShowUpgradeCta(false);
    };

    return (
        <CallToActionContext.Provider
            value={{
                showSignInView,
                hideSignInView,
                setSignInView,
                showUpgradeCta,
                hideUpgradeCta,
                upgradeCtaContextType: upgradeCta,
                setUpgradeCtaContext: setUpgradeCtaContext,
            }}
        >
            {children}
        </CallToActionContext.Provider>
    );
}

export const useCallToActionContext = () => useContext(CallToActionContext);

export default CallToActionContext;
